import { useState, useEffect } from "react";
import "./css/adminempdetail.css";
import {
  getEmployeeDetail,
  changeEmployeePassword,
  updateEmployeeInfo,
  checkDuplicateLoginId,
} from "../../../utils/adminEmployeeService";
import { apiGet } from "../../../utils/apiClient";

const AdminEmpDetail = ({ onClose, employeeId, onSuccess, initialEditMode = false }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);

  // 수정 폼 상태
  const [editId, setEditId] = useState("");
  const [editNm, setEditNm] = useState("");
  const [editTelNo, setEditTelNo] = useState("");
  const [idChecked, setIdChecked] = useState(null);
  const [idCheckLoading, setIdCheckLoading] = useState(false);

  // 권한 선택 상태
  const [authList, setAuthList] = useState([]);
  const [selectedAuthMenus, setSelectedAuthMenus] = useState([]); // 선택된 auth의 메뉴 풀
  const [checkedMenus, setCheckedMenus] = useState([]); // 체크된 메뉴
  const [selectedAuthNm, setSelectedAuthNm] = useState("");

  // 비밀번호 변경 상태
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [detailResult, authResponse] = await Promise.all([
        getEmployeeDetail(employeeId),
        apiGet("/admin/auth"),
      ]);

      if (detailResult.success) {
        setDetail(detailResult.data);
        setEditId(detailResult.data.empId);
        setEditNm(detailResult.data.empNm);
        setEditTelNo(detailResult.data.empTelNo || "");
        setSelectedAuthNm(detailResult.data.empAuthNm || "");
        setCheckedMenus(
          (detailResult.data.empMenuAuth || []).map((m) => m.code)
        );
      } else {
        alert("직원 정보를 불러오지 못했습니다.");
        onClose();
      }

      if (authResponse.ok) {
        const data = await authResponse.json();
        const mapped = data.map((a) => ({
          id: a.authIdx,
          name: a.authNm,
          authType: a.authType,
          menus: a.menuAuth,
        }));
        setAuthList(mapped);
        if (initialEditMode) {
          const adminAuth = mapped.find((a) => a.authType === "관리자");
          if (adminAuth) {
            setSelectedAuthMenus(adminAuth.menus || []);
            setSelectedAuthNm(adminAuth.name);
          }
        }
      }

      if (initialEditMode) setIsEditMode(true);
      setLoading(false);
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);


  const handleMenuCheck = (code) => {
    setCheckedMenus((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleIdChange = (v) => {
    setEditId(v);
    setIdChecked(null);
  };

  const handleCheckDuplicate = async () => {
    if (!editId.trim()) return;
    if (editId === detail.empId) {
      setIdChecked(true);
      return;
    }
    setIdCheckLoading(true);
    try {
      const isDuplicate = await checkDuplicateLoginId(editId);
      setIdChecked(!isDuplicate);
    } catch {
      setIdChecked(false);
    } finally {
      setIdCheckLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editId.trim() || !editNm.trim()) {
      alert("아이디와 이름을 입력해주세요.");
      return;
    }
    if (editId !== detail.empId && idChecked !== true) {
      alert("아이디 중복 확인을 해주세요.");
      return;
    }
    setSaveLoading(true);
    try {
      await updateEmployeeInfo(detail.empId, {
        currentEmpId: detail.empId,
        newEmpId: editId,
        empNm: editNm,
        empTelNo: editTelNo,
        empAuthNm: selectedAuthNm,
        menuAuth: checkedMenus,
      });
      alert("성공적으로 수정되었습니다.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      alert(error.message || "수정 중 오류가 발생했습니다.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPassword.length < 8) {
      setPwError("비밀번호는 최소 8자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setPwLoading(true);
    try {
      await changeEmployeePassword({ empId: employeeId, newPw: newPassword });
      alert("비밀번호가 성공적으로 변경되었습니다.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setPwError(error.message || "비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setPwLoading(false);
    }
  };

const handleEnterEditMode = () => {
    setIsEditMode(true);
    const adminAuth = authList.find((a) => a.authType === "관리자");
    if (adminAuth) {
      setSelectedAuthMenus(adminAuth.menus || []);
      setSelectedAuthNm(adminAuth.name);
    }
  };

  return (
    <div className="adminempdetail-backdrop">
      <div className="adminempdetail-wrap">
        <div className="adminempdetail-header">
          <span className="adminempdetail-title">직원 상세 정보</span>
          <div className="adminempdetail-header-btns">
            <button className="adminempdetail-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <div className="adminempdetail-loading">불러오는 중...</div>
        ) : (
          <div className="adminempdetail-body">
            {/* 기본 정보 */}
            <div className="adminempdetail-section">
              <div className="adminempdetail-section-title">기본 정보</div>
              <div className="adminempdetail-info-grid">
                {/* 아이디 */}
                <div className="adminempdetail-info-row">
                  <span className="adminempdetail-label">아이디</span>
                  {isEditMode ? (
                    <div className="adminempdetail-id-wrap">
                      <input
                        className="adminempdetail-input"
                        value={editId}
                        onChange={(e) => handleIdChange(e.target.value)}
                      />
                      <button
                        className="adminempdetail-action-btn"
                        onClick={handleCheckDuplicate}
                        disabled={idCheckLoading}
                      >
                        중복확인
                      </button>
                      {idChecked === true && (
                        <span className="adminempdetail-id-ok">사용 가능</span>
                      )}
                      {idChecked === false && (
                        <span className="adminempdetail-id-dup">사용 불가</span>
                      )}
                    </div>
                  ) : (
                    <span className="adminempdetail-value">{detail.empId}</span>
                  )}
                </div>

                {/* 비밀번호 */}
                <div className="adminempdetail-info-row">
                  <span className="adminempdetail-label">비밀번호</span>
                  <form className="adminempdetail-pw-inline-form" onSubmit={handleChangePw}>
                    <input
                      type="password"
                      className="adminempdetail-input"
                      placeholder="새 비밀번호 (8자 이상)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <div className="adminempdetail-input-btn-row">
                      <input
                        type="password"
                        className="adminempdetail-input"
                        placeholder="비밀번호 확인"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button type="submit" className="adminempdetail-action-btn" disabled={pwLoading}>
                        {pwLoading ? "변경 중..." : "비밀번호 변경"}
                      </button>
                    </div>
                    {pwError && <p className="adminempdetail-error adminempdetail-error-inline">{pwError}</p>}
                  </form>
                </div>

                {/* 이름 */}
                <div className="adminempdetail-info-row">
                  <span className="adminempdetail-label">이름</span>
                  {isEditMode ? (
                    <input
                      className="adminempdetail-input"
                      value={editNm}
                      onChange={(e) => setEditNm(e.target.value)}
                    />
                  ) : (
                    <span className="adminempdetail-value">{detail.empNm}</span>
                  )}
                </div>

                <div className="adminempdetail-info-row">
                  <span className="adminempdetail-label">연락처</span>
                  {isEditMode ? (
                    <input
                      className="adminempdetail-input"
                      value={editTelNo}
                      onChange={(e) => setEditTelNo(e.target.value)}
                    />
                  ) : (
                    <span className="adminempdetail-value">{detail.empTelNo}</span>
                  )}
                </div>
                <div className="adminempdetail-info-row">
                  <span className="adminempdetail-label">등록일</span>
                  <span className="adminempdetail-value">
                    {detail.empRegDate
                      ? new Date(detail.empRegDate).toLocaleDateString("ko-KR")
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* 권한 */}
            <div className="adminempdetail-section">
              <div className="adminempdetail-section-title">메뉴 권한</div>
              {isEditMode ? (
                <div className="adminempdetail-auth-edit">
                  <div className="adminempdetail-menu-checkboxes">
                    {selectedAuthMenus.map((menu) => (
                      <label key={menu.code} className="adminempdetail-menu-check-label">
                        <input
                          type="checkbox"
                          checked={checkedMenus.includes(menu.code)}
                          onChange={() => handleMenuCheck(menu.code)}
                        />
                        {menu.displayName}
                      </label>
                    ))}
                  </div>
                  {selectedAuthMenus.length === 0 && (
                    <p className="adminempdetail-auth-hint">권한 정보를 불러오는 중입니다...</p>
                  )}
                </div>
              ) : (
                <div className="adminempdetail-menu-list">
                  {!detail.empMenuAuth || detail.empMenuAuth.length === 0 ? (
                    <span className="adminempdetail-no-menu">없음</span>
                  ) : (
                    detail.empMenuAuth.map((menu) => (
                      <span key={menu.code} className="adminempdetail-menu-badge">
                        {menu.displayName}
                      </span>
                    ))
                  )}
                </div>
              )}
            </div>

          </div>
        )}

        {/* 푸터 버튼 */}
        {!loading && (
          <div className="adminempdetail-footer">
            {!isEditMode ? (
              <button className="adminempdetail-edit-btn" onClick={handleEnterEditMode}>
                수정
              </button>
            ) : (
              <>
                <button
                  className="adminempdetail-save-btn"
                  onClick={handleSave}
                  disabled={saveLoading}
                >
                  {saveLoading ? "저장 중..." : "저장"}
                </button>
                <button
                  className="adminempdetail-cancel-btn"
                  onClick={onClose}
                >
                  취소
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmpDetail;

